import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, useScroll, useSpring } from 'framer-motion';
import { BRAND } from '../data/products';
import ThemeSwitcher from './ThemeSwitcher';
import CursorRing from './motion/CursorRing';
import IntroCurtain from './IntroCurtain';

const NAV = [
  { to: '/products', label: 'Products' },
  { to: '/sets', label: 'Sets' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

export const Layout = ({ children }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-mb text-mb flex flex-col" data-testid="site-root">
      <IntroCurtain />
      <CursorRing />
      <ScrollProgress />
      <TopNav open={open} setOpen={setOpen} />
      <main className="flex-1 pt-[72px]" data-testid="main-content">
        {children}
      </main>
      <Footer />
    </div>
  );
};

const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 140, damping: 26 });
  return (
    <motion.div
      aria-hidden="true"
      style={{ scaleX }}
      className="fixed top-0 left-0 right-0 h-[2px] bg-gold origin-left z-[80]"
      data-testid="scroll-progress"
    />
  );
};

const TopNav = ({ open, setOpen }) => (
  <header
    className="fixed top-0 left-0 right-0 z-50 bg-mb hairline-b border-subtle"
    data-testid="top-nav"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
      <Link
        to="/"
        className="font-display text-[15px] tracking-[0.32em] uppercase text-mb hover:text-gold transition-colors"
        data-magnetic
        data-testid="brand-logo"
      >
        {BRAND.name}
      </Link>

      <nav className="hidden md:flex items-center gap-10">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`nav-${n.label.toLowerCase()}`}
            className={({ isActive }) =>
              `eyebrow relative transition-colors group ${
                isActive ? 'text-gold' : 'text-mb hover:text-gold'
              }`
            }
          >
            {({ isActive }) => (
              <>
                {n.label}
                <span
                  className={`absolute left-0 -bottom-2 h-[0.5px] bg-gold transition-all duration-300 ${
                    isActive ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                />
              </>
            )}
          </NavLink>
        ))}
        <ThemeSwitcher />
      </nav>

      <div className="md:hidden flex items-center gap-3">
      <ThemeSwitcher />
      <button
        className="text-mb p-2"
        onClick={() => setOpen(!open)}
        data-testid="mobile-menu-toggle"
        aria-label="Menu"
      >
        {open ? <X size={20} /> : <Menu size={20} />}
      </button>
      </div>
    </div>

    {open && (
      <div
        className="md:hidden hairline-t border-subtle bg-mb"
        data-testid="mobile-menu"
      >
        <nav className="flex flex-col px-6 py-8 gap-6">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `eyebrow ${isActive ? 'text-gold' : 'text-mb'}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
      </div>
    )}
  </header>
);

const Footer = () => (
  <footer
    className="hairline-t border-subtle mt-24"
    data-testid="site-footer"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-4 gap-12">
      <div>
        <div className="font-display text-[14px] tracking-[0.32em] uppercase text-mb">
          {BRAND.name}
        </div>
        <p className="mt-6 font-display italic text-[17px] text-mb-muted leading-relaxed">
          {BRAND.tagline}
        </p>
      </div>

      <div>
        <div className="eyebrow text-mb-muted mb-5">Shop</div>
        <ul className="space-y-3">
          <li>
            <Link to="/sets" className="text-[13px] text-mb hover:text-gold transition-colors">
              Sets
            </Link>
          </li>
          <li>
            <Link to="/products" className="text-[13px] text-mb hover:text-gold transition-colors">
              Individual pieces
            </Link>
          </li>
        </ul>
      </div>

      <div>
        <div className="eyebrow text-mb-muted mb-5">Atelier</div>
        <ul className="space-y-3">
          <li>
            <Link to="/about" className="text-[13px] text-mb hover:text-gold transition-colors">
              About
            </Link>
          </li>
          <li>
            <Link to="/contact" className="text-[13px] text-mb hover:text-gold transition-colors">
              Contact
            </Link>
          </li>
        </ul>
      </div>

      <div>
        <div className="eyebrow text-mb-muted mb-5">Enquiries</div>
        <a
          href={`mailto:${BRAND.email}`}
          className="block text-[13px] text-mb hover:text-gold transition-colors"
        >
          {BRAND.email}
        </a>
        <a
          href={`tel:${BRAND.phone.replace(/\s/g, '')}`}
          className="block mt-2 text-[13px] text-mb hover:text-gold transition-colors"
        >
          {BRAND.phone}
        </a>
        <div className="mt-2 text-[13px] text-mb-muted">{BRAND.address}</div>
      </div>
    </div>

    <div className="hairline-t border-subtle">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
        <div className="eyebrow-sm text-mb-muted">
          © {new Date().getFullYear()} {BRAND.name}
        </div>
        <div className="flex items-center gap-6">
          <Link to="/admin" className="eyebrow-sm text-mb-muted hover:text-gold transition-colors" data-testid="footer-admin-link">
            Atelier
          </Link>
          <div className="eyebrow-sm text-mb-muted">Leather, quietly.</div>
        </div>
      </div>
    </div>
  </footer>
);

export default Layout;
